import React, { useCallback, useMemo, type ReactNode } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import ChevronRightIcon from '@atlaskit/icon/glyph/chevron-right';
import ErrorIcon from '@atlaskit/icon/glyph/error';
import InfoIcon from '@atlaskit/icon/glyph/info';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import { Box, Inline, Stack } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import { colors } from '@atlaskit/theme';
import Toggle from '@atlaskit/toggle';
import { token } from '@atlaskit/tokens';
import { ff } from '@atlassian/jira-feature-flagging';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';

import AttachmentIllustration from '../../../common/assets/AttachmentIllustration.svg';
import CommentsIllustration from '../../../common/assets/CommentsIllustration.svg';
import FieldIllustration from '../../../common/assets/FieldIllustration.svg';
import HierarchyIllustration from '../../../common/assets/HierarchyIllustration.svg';
import ReviewImportUsers from '../../../common/assets/ReviewImportUsers.svg';
import { REVIEW_SCREEN_CARD_TEST_ID } from '../../../common/constants';
import type { SupportedThirdParty } from '../../../common/constants/import-type';
import { ReviewTilesType } from '../../../common/constants/steps';
import type { ThirdPartyImportSettings } from '../../../common/types';
import { Card } from '../../../common/ui/review-tile-card';
import { useContentForThirdParty, useUrlForThirdParty } from '../../../common/utils';
import { getHierarchyMappingItems } from '../../../common/utils/third-party-mapping';
import { useThirdPartyFieldsIncludedState } from '../../../controllers/import-state';
import { useThirdPartyHierarchyState } from '../../../controllers/import-state/selectors';
import { useThirdPartyMappedStatus } from '../../../controllers/import-state/selectors/third-party-status-mapping-state';
import messages from './messages';

export type Props = {
	onSetSelectedTile: (selectedTile: ReviewTilesType) => void;
	thirdParty: SupportedThirdParty;
	thirdPartyImportSettings: ThirdPartyImportSettings;
	isLoading: boolean;
	hasErrorOccurred: boolean;
	isStatusMappingLoading?: boolean;
	hasStatusMappingErrorOccurred?: boolean;
	onRefetchStatusMapping?: () => void;
	onRefetchFieldMapping: () => void;
	onSetThirdPartyImportSettings: (
		thirdPartyImportSettings: Partial<ThirdPartyImportSettings>,
	) => void;
	tiles: ReviewTilesType[];
};

const LoadingStatus = ({ loadingMessage }: { loadingMessage?: string }) => (
	<>
		<SpinnerWrapper>
			<Spinner size="small" />
		</SpinnerWrapper>
		<span>{loadingMessage}</span>
	</>
);

const ErrorStatus = ({ errorMessage }: { errorMessage: ReactNode }) => (
	<>
		<ErrorIcon label="" size="medium" primaryColor={token('color.icon.danger', '#EF5C48')} />
		<ErrorMessageWrapper>{errorMessage}</ErrorMessageWrapper>
	</>
);

export const ReviewTiles = ({
	onSetSelectedTile,
	thirdParty,
	thirdPartyImportSettings,
	onSetThirdPartyImportSettings,
	isLoading,
	hasErrorOccurred,
	isStatusMappingLoading = false,
	hasStatusMappingErrorOccurred = false,
	onRefetchFieldMapping,
	onRefetchStatusMapping,
	tiles,
}: Props) => {
	const { formatMessage } = useIntl();
	const {
		fieldsTileLoadingDescription,
		hierarchyTileDescription,
		usersTileHeading,
		usersTileDescriptionUnchecked,
		usersTileDescription,
		fieldsTileInfoDescription,
		hierarchyMappingTileDescription,
		hierarchyTileLoadingDescription,
		attachmentTileEnabledDescription,
	} = useContentForThirdParty(thirdParty);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [{ numThirdPartyFields: numFields, numFieldsIncluded, hasUnsupportedFieldFiltered }] =
		useThirdPartyFieldsIncludedState();
	const shouldDisableFieldsOrHierarchyTile = isLoading || hasErrorOccurred;
	const shouldDisableStatusTile = isStatusMappingLoading || hasStatusMappingErrorOccurred;
	const { fieldMappingURL } = useUrlForThirdParty(thirdParty);
	const [{ numMappedStatuses, numTotalStatuses }] = useThirdPartyMappedStatus();

	const [thirdPartyHierarchyState] = useThirdPartyHierarchyState({
		types: getHierarchyMappingItems(thirdParty),
		formatMessage,
	});

	// remove fg('jwm_clickup_importer') when clean up ff('jwm-x2c-hierarchy-mapping_kkjnd')
	const enableHierarchyMapping =
		ff('jwm-x2c-hierarchy-mapping_kkjnd') || fg('jwm_clickup_importer');
	const handleHierarchyTileClick = useCallback(() => {
		if (enableHierarchyMapping && shouldDisableFieldsOrHierarchyTile) {
			return;
		}
		const analyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'projectHierarchyButton',
		});

		fireUIAnalytics(analyticsEvent);
		onSetSelectedTile(ReviewTilesType.HIERARCHY);
	}, [
		createAnalyticsEvent,
		enableHierarchyMapping,
		onSetSelectedTile,
		shouldDisableFieldsOrHierarchyTile,
	]);

	const handleFieldMappingTileClick = useCallback(() => {
		if (shouldDisableFieldsOrHierarchyTile) {
			return;
		}

		const analyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'fieldMappingButton',
		});

		fireUIAnalytics(analyticsEvent);
		onSetSelectedTile(ReviewTilesType.FIELDS);
	}, [createAnalyticsEvent, onSetSelectedTile, shouldDisableFieldsOrHierarchyTile]);

	const handleStatusMappingTileClick = useCallback(() => {
		if (shouldDisableStatusTile) {
			return;
		}
		const analyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'statusMappingButton',
		});

		fireUIAnalytics(analyticsEvent);
		onSetSelectedTile(ReviewTilesType.STATUS);
	}, [createAnalyticsEvent, onSetSelectedTile, shouldDisableStatusTile]);

	const descriptionForHierarchyTile = useMemo(() => {
		if (isLoading) {
			return <LoadingStatus loadingMessage={hierarchyTileLoadingDescription} />;
		}

		if (hasErrorOccurred) {
			return (
				<ErrorStatus
					errorMessage={formatMessage(messages.hierarchyTileErrorDescription, {
						link: (chunks: ReactNode[]) => (
							<Button appearance="link" spacing="none" onClick={onRefetchFieldMapping}>
								{chunks}
							</Button>
						),
					})}
				/>
			);
		}

		return hierarchyMappingTileDescription
			? formatMessage(hierarchyMappingTileDescription, {
					jiraHierarchyItem: thirdPartyHierarchyState?.selectedHierarchyItem,
				})
			: null;
	}, [
		formatMessage,
		hasErrorOccurred,
		hierarchyMappingTileDescription,
		hierarchyTileLoadingDescription,
		isLoading,
		onRefetchFieldMapping,
		thirdPartyHierarchyState,
	]);

	const descriptionForFieldsTile = useMemo(() => {
		if (isLoading) {
			return <LoadingStatus loadingMessage={fieldsTileLoadingDescription} />;
		}

		if (hasErrorOccurred) {
			return (
				<ErrorStatus
					errorMessage={formatMessage(messages.fieldTileErrorDescription, {
						link: (chunks: ReactNode[]) => (
							<Button appearance="link" spacing="none" onClick={onRefetchFieldMapping}>
								{chunks}
							</Button>
						),
					})}
				/>
			);
		}

		return (
			<Stack space="space.050">
				<Box>{formatMessage(messages.fieldTileDescription, { numFields, numFieldsIncluded })}</Box>
				{hasUnsupportedFieldFiltered !== false && fieldsTileInfoDescription && (
					<Inline space="space.025">
						<InfoIcon
							label=""
							size="medium"
							primaryColor={token('color.icon.information', colors.B200)}
						/>
						<span>
							{formatMessage(fieldsTileInfoDescription, {
								link: (chunks: ReactNode) => (
									<a
										href={fieldMappingURL}
										target="_blank"
										onClick={(event) => event.stopPropagation()}
									>
										{chunks}
									</a>
								),
							})}
						</span>
					</Inline>
				)}
			</Stack>
		);
	}, [
		fieldMappingURL,
		fieldsTileInfoDescription,
		fieldsTileLoadingDescription,
		formatMessage,
		hasErrorOccurred,
		hasUnsupportedFieldFiltered,
		isLoading,
		numFields,
		numFieldsIncluded,
		onRefetchFieldMapping,
	]);

	const descriptionForStatusTile = useMemo(() => {
		if (isStatusMappingLoading) {
			return (
				<LoadingStatus
					loadingMessage={formatMessage(messages.statusTileLoadingDescriptionNonFinal)}
				/>
			);
		}

		if (hasStatusMappingErrorOccurred) {
			return (
				<ErrorStatus
					errorMessage={formatMessage(messages.statusTileErrorDescriptionNonFinal, {
						link: (chunks: ReactNode[]) => (
							<Button appearance="link" spacing="none" onClick={onRefetchStatusMapping}>
								{chunks}
							</Button>
						),
					})}
				/>
			);
		}

		return (
			<Stack space="space.050">
				<Box>
					{formatMessage(messages.statusTileDescription, {
						numStatusesIncluded: numMappedStatuses,
						numStatuses: numTotalStatuses,
					})}
				</Box>
			</Stack>
		);
	}, [
		formatMessage,
		hasStatusMappingErrorOccurred,
		isStatusMappingLoading,
		numMappedStatuses,
		numTotalStatuses,
		onRefetchStatusMapping,
	]);

	const descriptionForUsersTile = useMemo(() => {
		if (!thirdPartyImportSettings.shouldImportUsers && usersTileDescriptionUnchecked) {
			return (
				<Stack space="space.050">
					<Inline>
						<WarningIcon
							label=""
							size="medium"
							primaryColor={token('color.icon.warning', colors.Y400)}
						/>
						<span>{usersTileDescriptionUnchecked}</span>
					</Inline>
				</Stack>
			);
		}
		if (usersTileDescription && usersTileDescription !== '') {
			return (
				<Stack space="space.050">
					<Box>{usersTileDescription}</Box>
				</Stack>
			);
		}
		return null;
	}, [
		thirdPartyImportSettings.shouldImportUsers,
		usersTileDescription,
		usersTileDescriptionUnchecked,
	]);

	const descriptionForAttachmentTile = useMemo(() => {
		if (thirdPartyImportSettings.shouldImportAttachments && attachmentTileEnabledDescription) {
			return (
				<Stack space="space.050">
					<Inline alignBlock="center">
						<WarningIcon
							label=""
							size="medium"
							primaryColor={token('color.icon.warning', colors.Y400)}
						/>
						<span>{attachmentTileEnabledDescription}</span>
					</Inline>
				</Stack>
			);
		}
		return null;
	}, [thirdPartyImportSettings.shouldImportAttachments, attachmentTileEnabledDescription]);

	const descriptionForCommentsTile = useMemo(() => {
		if (thirdPartyImportSettings.shouldImportComments) {
			return (
				<Stack space="space.050">
					<Inline>
						<WarningIcon
							label=""
							size="medium"
							primaryColor={token('color.icon.warning', colors.Y400)}
						/>
						<span>{formatMessage(messages.commentsTileEnabledDescription)}</span>
					</Inline>
				</Stack>
			);
		}
		return null;
	}, [formatMessage, thirdPartyImportSettings.shouldImportComments]);

	const tilesMap = useMemo(
		() => ({
			[ReviewTilesType.HIERARCHY]: {
				key: ReviewTilesType.HIERARCHY,
				heading: formatMessage(messages.hierarchyTileHeading),
				description: enableHierarchyMapping
					? descriptionForHierarchyTile
					: hierarchyTileDescription,
				icon: (
					<Icon
						src={HierarchyIllustration}
						aria-label={formatMessage(messages.hierarchyTileLabel)}
						role="img"
						alt=""
					/>
				),
				rightActions: (
					<ChevronRightIcon
						label=""
						size="large"
						primaryColor={
							shouldDisableFieldsOrHierarchyTile && enableHierarchyMapping
								? token('color.icon.disabled', colors.N200)
								: token('color.icon.information', colors.B200)
						}
					/>
				),
				onClick: handleHierarchyTileClick,
			},
			[ReviewTilesType.STATUS]: {
				key: ReviewTilesType.STATUS,
				heading: formatMessage(messages.statusTileLabel),
				description: descriptionForStatusTile,
				icon: (
					<Icon
						src={FieldIllustration}
						aria-label={formatMessage(messages.statusTileLabel)}
						role="img"
						alt=""
					/>
				),
				rightActions: (
					<ChevronRightIcon
						label=""
						size="large"
						primaryColor={
							shouldDisableStatusTile
								? token('color.icon.disabled', colors.N200)
								: token('color.icon.information', colors.B200)
						}
					/>
				),
				onClick: handleStatusMappingTileClick,
			},
			[ReviewTilesType.FIELDS]: {
				key: ReviewTilesType.FIELDS,
				heading: formatMessage(messages.fieldTileHeading),
				description: descriptionForFieldsTile,
				icon: (
					<Icon
						src={FieldIllustration}
						aria-label={formatMessage(messages.fieldTileLabel)}
						role="img"
						alt=""
					/>
				),
				rightActions: (
					<ChevronRightIcon
						label=""
						size="large"
						primaryColor={
							shouldDisableFieldsOrHierarchyTile
								? token('color.icon.disabled', colors.N200)
								: token('color.icon.information', colors.B200)
						}
					/>
				),
				onClick: handleFieldMappingTileClick,
			},
			[ReviewTilesType.ATTACHMENTS]: {
				key: ReviewTilesType.ATTACHMENTS,
				heading: formatMessage(messages.attachmentTileHeading),
				description: descriptionForAttachmentTile,
				icon: (
					<Icon
						src={AttachmentIllustration}
						aria-label={formatMessage(messages.attachmentTileLabel)}
						role="img"
						alt=""
					/>
				),
				rightActions: (
					<Toggle
						onChange={(value) =>
							onSetThirdPartyImportSettings({
								shouldImportAttachments: value.target.checked,
							})
						}
						isChecked={thirdPartyImportSettings.shouldImportAttachments}
					/>
				),
			},
			[ReviewTilesType.USERS]: {
				key: ReviewTilesType.USERS,
				heading: usersTileHeading || '',
				description: descriptionForUsersTile,
				icon: (
					<Icon
						src={ReviewImportUsers}
						aria-label={formatMessage(messages.usersTileLabel)}
						role="img"
						alt=""
					/>
				),
				rightActions: (
					<Toggle
						onChange={(value) =>
							onSetThirdPartyImportSettings({
								shouldImportUsers: value.target.checked,
							})
						}
						isChecked={thirdPartyImportSettings.shouldImportUsers}
					/>
				),
			},
			[ReviewTilesType.COMMENTS]: {
				key: ReviewTilesType.COMMENTS,
				heading: formatMessage(messages.commentsTileHeading),
				description: descriptionForCommentsTile,
				icon: (
					<Icon
						src={CommentsIllustration}
						aria-label={formatMessage(messages.commentsTileLabel)}
						role="img"
						alt=""
					/>
				),
				rightActions: (
					<Toggle
						onChange={(value) =>
							onSetThirdPartyImportSettings({
								shouldImportComments: value.target.checked,
							})
						}
						isChecked={thirdPartyImportSettings.shouldImportComments}
					/>
				),
			},
		}),
		[
			descriptionForAttachmentTile,
			descriptionForCommentsTile,
			descriptionForFieldsTile,
			descriptionForHierarchyTile,
			descriptionForStatusTile,
			descriptionForUsersTile,
			enableHierarchyMapping,
			formatMessage,
			handleFieldMappingTileClick,
			handleHierarchyTileClick,
			handleStatusMappingTileClick,
			hierarchyTileDescription,
			onSetThirdPartyImportSettings,
			shouldDisableFieldsOrHierarchyTile,
			shouldDisableStatusTile,
			thirdPartyImportSettings.shouldImportAttachments,
			thirdPartyImportSettings.shouldImportComments,
			thirdPartyImportSettings.shouldImportUsers,
			usersTileHeading,
		],
	);

	return (
		<Wrapper>
			{tiles
				.map((type) => tilesMap[type])
				.map(({ key, ...props }) => (
					<Card key={key} {...props} testId={REVIEW_SCREEN_CARD_TEST_ID} />
				))}
		</Wrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	width: '100%',
	padding: `${token('space.300', '24px')} 0`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Icon = styled.img({
	width: '60%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SpinnerWrapper = styled.span({
	marginRight: token('space.100', '8px'),
	height: token('space.300', '24px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ErrorMessageWrapper = styled.span({
	alignSelf: 'center',
});
